// Quiet React context warnings in development.
if (import.meta.env.DEV) {
  // These are deprecation warnings, and they do matter for future development, but they are very noisy day-to-day.
  const SUPPRESSED_WARNINGS = ['legacy contextTypes API', 'legacy childContextTypes API', 'defaultProps will be removed', 'findDOMNode is deprecated']

  const filterSuppressed = (logger) => (message, ...args) => {
    if (!SUPPRESSED_WARNINGS.some((entry) => typeof message === 'string' && message.includes(entry))) {
      logger(message, ...args)
    }
  }

  console.warn = filterSuppressed(console.warn)
  console.error = filterSuppressed(console.error)
}

import { connect } from '@cerebral/react'
import { StyledEngineProvider, ThemeProvider } from '@mui/material'
import jssPreset from '@mui/styles/jssPreset'
import StylesProvider from '@mui/styles/StylesProvider'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider'
import { sequences, state } from 'cerebral'
import { create } from 'jss'
import { Info, Settings } from 'luxon'
import React, { useEffect } from 'react'
import Favicon from 'react-favicon'
import { Helmet } from 'react-helmet'
import brand from '../lib/util/brand'
import { createMUITheme } from '../theme'
import Layout from './layout/Layout'
import Router from './views/Router'

const { ENVIRONMENT_NAME } = import.meta.env

Settings.defaultWeekSettings = {
  firstDay: 7,
  minimalDays: 1,
  weekend: Info.getWeekendWeekdays(),
}

const jss = create({ plugins: jssPreset().plugins })

const defaultTitle = `Manage - ${brand?.name}`
const titleTemplate = `%s - ${defaultTitle}`

const theme = createMUITheme({ responsiveFontSizes: true })

const App = connect(
  {
    checkAccount: sequences`account.reauthenticate`,
    currentUser: state`account.currentUser`,
    getInfo: sequences`info.getInfo`,
    requestingCount: state`locationRouteRequests.requestingCount`,
    removeInvitationParams: sequences`account.removeInvitationParams`,
    invitationParams: state`account.invitationParams`,
    organization: state`account.organization`,
  },
  ({ checkAccount, currentUser, getInfo, requestingCount, removeInvitationParams, invitationParams, organization }) => {
    useEffect(() => {
      checkAccount()
    }, [])

    useEffect(() => {
      if (currentUser?.canAccessCommandCenter) {
        getInfo()
        if (invitationParams) {
          removeInvitationParams()
        }
      }

      // Set resort and parent resort in GA
      if (organization) {
        const userProperties = {
          resort: organization.slug,
          ...(organization.parent && { parent_resort: organization.parent?.slug }),
        }
        window.gtag('set', 'user_properties', userProperties)
      }
    }, [currentUser?.canAccessCommandCenter])

    const countPrefix = requestingCount ? `(${requestingCount}) ` : ''

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-US">
            <StylesProvider jss={jss}>
              <Helmet titleTemplate={`${countPrefix}${titleTemplate}`} defaultTitle={`${countPrefix}${defaultTitle}`} defer={false} />
              {Boolean(brand) && (
                <Favicon
                  url={`/favicon-${brand.slug}.svg`}
                  alertCount={requestingCount}
                  renderOverlay={(canvas, context) => {
                    // Icon color adjustment for staging and development.
                    if (ENVIRONMENT_NAME !== 'production') {
                      context.globalCompositeOperation = brand.slug === 'quiltt' || brand.slug === 'campushub' ? 'screen' : 'color'
                      context.fillStyle = ENVIRONMENT_NAME === 'staging' ? theme.palette.grey[900] : theme.palette.error.main
                      context.fillRect(0, 0, canvas.width, canvas.height)
                    }
                  }}
                  size={96}
                />
              )}
              <Layout>
                <Router />
              </Layout>
            </StylesProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    )
  }
)

export default App
